import { Award, Users, Target, Globe, CheckCircle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { SEO, getOrganizationSchema } from "../components/SEO";

const About = () => {
  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "We deliver world-class training that meets the highest international standards."
    },
    {
      icon: Users,
      title: "Expertise",
      description: "Our instructors are industry veterans with decades of practical experience."
    },
    {
      icon: Target,
      title: "Results-Focused",
      description: "We're committed to your success, with a 98% certification pass rate."
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Serving professionals across the United States, Jamaica, the Caribbean, and worldwide."
    }
  ];

  const milestones = [
    { year: "2016", title: "Founded", description: "Compliance Management Systems established in Kingston, Jamaica" },
    { year: "2021", title: "Caribbean & Global Expansion", description: "Extended services across the Caribbean region and international markets" },
    { year: "2023", title: "PECB Partnership", description: "Became an authorized PECB training partner" },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us | Consultancy & PECB Certification Training"
        description="Compliance Management Systems provides consultancy and PECB certification training in quality management, food safety, information security and risk management — helping organizations across the United States, Jamaica and the Caribbean for over 10 years."
        url="/about"
        keywords={[
          "Compliance Management Systems Jamaica",
          "PECB training partner",
          "ISO certification provider",
          "Caribbean training center",
          "Jamaica ISO training",
          "professional certification"
        ]}
        structuredData={getOrganizationSchema()}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-primary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-accent font-medium text-sm uppercase tracking-widest mb-4 block">
              About Us
            </span>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white tracking-tight mb-6" data-testid="about-title">
              Building Stronger Organizations Through Consultancy and Training
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-4" data-testid="about-intro">
              For over 10 years, Compliance Management Systems has helped organizations strengthen their
              management systems and professionals build the skills to lead with confidence.
            </p>
            <p className="text-lg text-white/80 leading-relaxed" data-testid="about-intro-2">
              We provide consultancy services and certification training in quality management, food safety,
              information security, and risk management. From developing and implementing management systems to
              preparing for certification, our practical, tailored approach helps clients meet requirements,
              improve performance, and achieve lasting results.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-accent font-medium text-sm uppercase tracking-widest mb-2 block">
                Our Mission
              </span>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary tracking-tight mb-6">
                Practical Expertise, From Implementation to Certification
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed" data-testid="mission-p1">
                Our mission is to help organizations build management systems that work in practice — not just on
                paper. Through hands-on consultancy, we guide clients through gap assessments, system design and
                implementation, internal and supplier audits, and readiness for certification.
              </p>
              <p className="text-muted-foreground mb-6 leading-relaxed" data-testid="mission-p2">
                Alongside our consultancy, we equip professionals with internationally recognized PECB certification
                training, so teams have the knowledge and confidence to lead, maintain and continually improve their
                systems long after the project is complete.
              </p>
              <div className="space-y-3">
                {["Tailored Consultancy & Implementation Support", "Internal, Supplier & Third-Party Audits", "Internationally Recognized PECB Certifications", "Ongoing Professional Support"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3184416/pexels-photo-3184416.jpeg"
                alt="Team meeting"
                className="rounded-2xl shadow-2xl"
              />
              {/* PECB Badge Overlay */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-comply/artifacts/a4vpv6m5_1-pecb-authorized-reseller-initiate.png" 
                  alt="PECB Authorized Partner" 
                  className="h-20 w-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent font-medium text-sm uppercase tracking-widest mb-2 block">
              Our Values
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary tracking-tight">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-border/50 hover:border-brand/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-7 w-7 text-brand" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent font-medium text-sm uppercase tracking-widest mb-2 block">
              Our Journey
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary tracking-tight">
              Milestones
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center font-heading font-bold text-sm">
                      {milestone.year}
                    </div>
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 h-full bg-border mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <h3 className="font-heading font-semibold text-lg">{milestone.title}</h3>
                    <p className="text-muted-foreground">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PECB Partnership */}
      <section className="py-16 lg:py-24 bg-brand">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white tracking-tight mb-6">
              Authorized PECB Training Partner
            </h2>
            <p className="text-lg text-white/80 mb-8">
              As an official PECB training partner, we deliver certified courses that meet 
              international standards. Our certifications are recognized globally, giving 
              our graduates a competitive edge in the job market.
            </p>
            <div className="flex flex-wrap justify-center gap-8">
              <div className="text-center">
                <div className="font-heading font-bold text-4xl text-white mb-1">38+</div>
                <div className="text-white/80 text-sm">Certified Courses</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-bold text-4xl text-white mb-1">98%</div>
                <div className="text-white/80 text-sm">Pass Rate</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-bold text-4xl text-white mb-1">20+</div>
                <div className="text-white/80 text-sm">Countries Served</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
